/**
 * Gelände & Lufträume — what is under the route and what is around it.
 *
 * Three blocks, in the order a pilot asks: the **profile** (does the planned
 * altitude clear the ground), the **legs** (by how much, and where), the
 * **airspace** (who has to be called before entering). One caveat sentence at
 * the foot covers all three, because they are all advisory and saying so three
 * times teaches people to skip it.
 *
 * Everything here is read from contexts that fetch by themselves — the terrain
 * profile from `TerrainProvider`, the crossings from `useAirspace()` — so there
 * is no button that says "check". What the pane owns is one pilot decision (how
 * wide a corridor the ground is read over) and one pilot action (take the
 * suggested Sicherheitshöhe into a cell you had overruled).
 *
 * Two things it deliberately does not do:
 *
 * **It never recomputes what the store already knows.** The planned altitude
 * comes through `checkAltitudes()`, the same function the airspace check uses, so
 * the line on the chart and the level the crossings were looked up at are the
 * same number by construction rather than by coincidence.
 *
 * **It writes nothing unasked.** `terrain.ts` already fills an empty (or still
 * terrain-owned) Sicherheitshöhe from the profile; a cell the pilot typed stays
 * theirs until they press `Sicherheitshöhe übernehmen` on that leg.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { destinationPoint, initialBearingDeg, midpoint } from '@avia/core';

import { EmptyState } from '../../app/EmptyState';
import { severityOf, usePublishBanners, type Banner } from '../../app/severity';
import { Freshness } from '../../components/Freshness';
import { useT } from '../../i18n';
import { useMapApi } from '../../map/mapBridge';
import { usePlanRev, usePlanStore, useRoute } from '../../planContext';
import { checkAltitudes } from '../../route';
import { DEFAULT_CORRIDOR_NM } from '../../terrain';
import { useTerrain } from '../../terrainContext';
import { AirspaceList } from './AirspaceList';
import { airspaceCounts, type AirspaceRow } from './airspace';
import { ClearanceHeader } from './ClearanceHeader';
import { CorridorControl } from './CorridorControl';
import { LegTerrainList, type LegRow } from './LegTerrainList';
import { ProfileChart } from './ProfileChart';
import { parseSafeAltFt, profileModel } from './profile';
import { useAirspace } from './useAirspace';
import './terrain.css';

export function TerrainPane() {
  const t = useT();
  const store = usePlanStore();
  const rev = usePlanRev();
  const route = useRoute();
  const terrain = useTerrain();
  const air = useAirspace();
  const map = useMapApi();
  const [selected, setSelected] = useState('');

  const waypoints = useMemo(() => route?.waypoints ?? [], [route]);
  const names = useMemo(() => waypoints.map(w => w.name), [waypoints]);

  /**
   * The altitude each waypoint is planned at — the map's own `alt_ft` first,
   * then the Höhe/FL cell of the leg that ends there, then nothing. Leg `n` is
   * drawn at the altitude of the waypoint it ends at, which is the cell
   * `leg{n}_alt` holds and the level the airspace check ran at.
   */
  const alts = useMemo(
    () => checkAltitudes(waypoints, leg => store.val('leg' + leg + '_alt')),
    // `rev` is the store's change signal: half of this comes out of the form
    [waypoints, store, rev],
  );

  const model = useMemo(
    () =>
      profileModel({
        legs: terrain?.profile?.legs ?? [],
        planned: n => alts[n] ?? null,
        suggested: n => parseSafeAltFt(store.val('leg' + n + '_safealt')),
      }),
    [terrain?.profile, alts, store, rev],
  );

  /** The per-leg list: the drawing's numbers, plus what the profile suggests. */
  const legRows = useMemo<LegRow[]>(() => {
    const byLeg = new Map((terrain?.rows ?? []).map(r => [r.leg, r]));
    return model.legs.map(p => {
      const tr = byLeg.get(p.leg);
      const id = 'leg' + p.leg + '_safealt';
      return {
        leg: p.leg,
        from: names[p.leg - 1] ?? '',
        to: names[p.leg] ?? '',
        p,
        msaFt: tr?.msaFt ?? null,
        msaRule: tr?.msaRule ?? '',
        cell: tr?.safeAlt ?? '',
        mine: store.val(id) !== '' && store.derivedSource(id) !== 'terrain',
        writable: tr?.writable ?? p.leg <= 12,
      };
    });
  }, [model.legs, terrain?.rows, names, store, rev]);

  /** The x axis: the waypoint's name and, where the form has one, its ETO. */
  const marks = useMemo(
    () =>
      model.marks.map(m => ({
        ident: names[m.index] ?? '',
        eto: store.val('cp' + (m.index + 1) + '_eto'),
      })),
    [model.marks, names, store, rev],
  );

  /* ---- what the pilot has to decide about ----
     The four inputs are reduced to primitives BEFORE the banner list is built,
     so the list keeps its identity across every keystroke that does not change
     one of them. `usePublishBanners` republishes on identity, and a banner bus
     that fires on every character typed into the form is a re-render of the
     whole banner region per character. */
  const counts = useMemo(() => airspaceCounts(air.legs), [air.legs]);
  const noneLeg = model.legs.find(l => l.level === 'none')?.leg ?? 0;
  const lowLeg = model.legs.find(l => l.level === 'low')?.leg ?? 0;
  const restricted = counts.restricted;
  const controlled = counts.controlled;

  const banners = useMemo<Banner[]>(() => {
    const out: Banner[] = [];
    if (noneLeg) {
      out.push({
        code: 'clearance-none',
        severity: severityOf('clearance-none'),
        key: 'terrain.bn.clearance-none',
        params: { leg: String(noneLeg) },
      });
    } else if (lowLeg) {
      out.push({
        code: 'clearance-low',
        severity: severityOf('clearance-low'),
        key: 'terrain.bn.clearance-low',
        params: { leg: String(lowLeg) },
      });
    }
    if (restricted > 0) {
      out.push({
        code: 'airspace-restricted',
        severity: severityOf('airspace-restricted'),
        key: 'terrain.bn.airspace-restricted',
        params: { n: String(restricted) },
      });
    }
    if (controlled > 0) {
      out.push({
        code: 'airspace-controlled',
        severity: severityOf('airspace-controlled'),
        key: 'terrain.bn.airspace-controlled',
        params: { n: String(controlled) },
      });
    }
    return out;
  }, [noneLeg, lowLeg, restricted, controlled]);

  usePublishBanners('terrain', banners);

  /* ---- the map ---- */
  const onShow = useCallback(
    (row: AirspaceRow) => {
      setSelected(row.id);
      map.setHighlight([row.id]);
      const a = waypoints[row.leg - 1];
      const b = waypoints[row.leg];
      if (!a || !b) return;
      const from = { lat: a.lat, lon: a.lon };
      const to = { lat: b.lat, lon: b.lon };
      const p =
        row.entryLegNm == null
          ? midpoint(from, to)
          : destinationPoint(from, initialBearingDeg(from, to), row.entryLegNm);
      map.flyTo(p.lat, p.lon);
    },
    [map, waypoints],
  );

  // leaving the tab must not leave one area painted on the canvas; the Strecke
  // pane re-paints the whole set from its own check when it comes back
  useEffect(() => () => map.setHighlight([]), [map]);

  const takeSafeAlt = useCallback(
    (leg: number, cell: string) => store.set('leg' + leg + '_safealt', cell),
    [store],
  );

  /** Every leg at once — the same write, for a plan that was overruled all through. */
  const takeAllSafeAlt = useCallback(
    (list: readonly { leg: number; cell: string }[]) => {
      for (const x of list) store.set('leg' + x.leg + '_safealt', x.cell);
    },
    [store],
  );

  /* ---- what the ⓘ carries: the published limits of both answers ---- */
  const limits = useMemo(() => {
    const codes = [...new Set([...(terrain?.profile?.assumptions ?? []), ...air.assumptions])];
    const list = codes.map(c => t('map.assume.' + c)).join(' · ');
    return [t('terrain.hint'), list ? t('map.check.assumptions') + ' ' + list : ''].filter(Boolean).join(' ');
  }, [terrain?.profile, air.assumptions, t]);

  if (waypoints.length < 2) {
    return <EmptyState title={t('terrain.empty.title')} line={t('terrain.empty.line')} />;
  }

  const hasProfile = !model.empty;

  return (
    <div className="aet">
      <section className="aet-section">
        <div className="aet-sechead">
          <h2 className="ae-eyebrow">{t('terrain.title')}</h2>
          <Freshness family="terrain" label={false} info={limits} />
        </div>

        {hasProfile ? (
          <>
            <ClearanceHeader model={model} />
            <ProfileChart model={model} stale={terrain?.busy === true} labels={marks} />
            <ul className="aet-legend">
              <li className="terrain">{t('terrain.legend.terrain')}</li>
              <li className="planned">{t('terrain.legend.planned')}</li>
              <li className="safe">{t('terrain.legend.safe')}</li>
              <li className="obst">{t('terrain.legend.obst')}</li>
            </ul>
          </>
        ) : terrain?.error ? (
          <p className="aet-note">{t('terrain.error')}</p>
        ) : (
          <p className="aet-note">{t('terrain.none')}</p>
        )}

        <CorridorControl
          value={terrain?.corridor ?? DEFAULT_CORRIDOR_NM}
          onChange={nm => terrain?.setCorridor(nm)}
        />

        {terrain && terrain.kept.length > 0 ? (
          <p className="aet-note">{t('terrain.kept', { legs: terrain.kept.join(', ') })}</p>
        ) : null}

        <LegTerrainList rows={legRows} onTake={takeSafeAlt} onTakeAll={takeAllSafeAlt} />
      </section>

      <section className="aet-section">
        <div className="aet-sechead">
          <h2 className="ae-eyebrow">{t('terrain.as.title')}</h2>
          {air.busy ? <span className="aet-note">{t('terrain.as.busy')}</span> : null}
        </div>
        {air.error && air.legs.length === 0 ? (
          <p className="aet-note">{t('terrain.as.error')}</p>
        ) : (
          <AirspaceList legs={air.legs} names={names} selected={selected} onShow={onShow} />
        )}
      </section>

      {/* one caveat for all three blocks — it is a safety statement, not provenance */}
      <p className="aet-caveat">{t('terrain.caveat')}</p>

      {/* openAIP asks for a credit wherever its data is shown, and this pane is
          full of it: the crossings, the obstacles, the classes. The map's own
          control carries the same line — but on a phone this pane covers the
          map, which is exactly when it is doing the showing. */}
      <p className="aet-credit">
        <a href="https://www.openaip.net/" target="_blank" rel="noopener noreferrer">
          {t('geo.credit')}
        </a>
      </p>
    </div>
  );
}

export default TerrainPane;
